import { useState } from "react";
import "./PPFCalculator.css";

const inr = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const PPF_RATE = 7.1;

const MILESTONES = {
  7:  { label: "Partial Withdrawal", color: "#0ea5e9" },
  15: { label: "Lock-in Ends",       color: "#7c3aed" },
  20: { label: "20-Year Mark",       color: "#394420" },
  25: { label: "25-Year Mark",       color: "#263012" },
  30: { label: "Max Maturity",       color: "#263012" },
};

const TABS = [
  { id: 0, label: "Investment", icon: "💰", desc: "Amount & timing" },
  { id: 1, label: "Duration",   icon: "📅", desc: "Tenure & extensions" },
  { id: 2, label: "Profile",    icon: "👤", desc: "Age & mode" },
  { id: 3, label: "Results",    icon: "📊", desc: "Your maturity" },
];

export default function PPFCalculator() {
  const [activeTab, setActiveTab]   = useState(0);
  const [form, setForm]             = useState({
    yearlyInvestment: 130010,
    tenure:           15,
    extensionBlocks:  0,
    startAge:         30,
    investmentMode:   "start",
  });
  const [result, setResult] = useState(null);

  const upd    = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const n      = (k)    => parseFloat(form[k]) || 0;
  const yearly = Math.min(n("yearlyInvestment"), 150010);

  function calculate() {
    const rate       = PPF_RATE / 100;
    const totalYears = form.tenure + form.extensionBlocks * 5;
    const startAge   = n("startAge");
    const modeStart  = form.investmentMode === "start";
    let balance = 0, totalInvested = 0;
    const rows = [];

    for (let y = 1; y <= totalYears; y++) {
      totalInvested += yearly;
      const interest = modeStart
        ? (balance + yearly) * rate
        : balance * rate + yearly * (rate / 2);
      balance = balance + yearly + interest;
      rows.push({
        year: y, age: startAge + y,
        invested: yearly,
        interest: Math.round(interest),
        balance:  Math.round(balance),
        cumInv:   Math.round(totalInvested),
        milestone: MILESTONES[y] || null,
      });
    }

    const maturity       = balance;
    const totalInterest  = maturity - totalInvested;
    const interestPct    = ((totalInterest / maturity) * 100).toFixed(1);
    const wealthMult     = (maturity / totalInvested).toFixed(2);
    const annualTaxSaving = Math.min(yearly, 150010) * 0.3;

    setResult({
      maturity, totalInvested, totalInterest,
      interestPct, wealthMult, totalYears,
      startAge, rows, annualTaxSaving, yearly,
    });
    setActiveTab(3);
  }

  const goNext = () => { if (activeTab < 2) setActiveTab(activeTab + 1); else calculate(); };
  const goPrev = () => { if (activeTab > 0) setActiveTab(activeTab - 1); };

  return (
    <div className="ppf-page">
      {/* ── HERO ── */}
      <div className="ppf-hero">
        <div className="ppf-hero-orb ppf-hero-orb--a" />
        <div className="ppf-hero-orb ppf-hero-orb--b" />
        <div className="ppf-hero-inner">
          <div className="ppf-hero-left">
            <span className="ppf-badge">
              <span className="ppf-badge-dot" />
              EEE · Triple Tax Exempt
            </span>
        <h1 className="hero-title">
 <span className="choose-title-accent"> PPF </span>Calculator 
</h1>
            <p>Public Provident Fund · Govt-backed · {PPF_RATE}% p.a. guaranteed</p>
            <div className="ppf-pills">
              <span>📋 80C Deduction</span>
              <span>💰 Tax-Free Interest</span>
              <span>🎁 Tax-Free Maturity</span>
            </div>
          </div>
   
        </div>
      </div>

      {/* ── MAIN SHELL ── */}
      <div className="ppf-shell">

        {/* ── STEP NAV ── */}
        <div className="ppf-steps">
          {TABS.map((tab) => {
            const isDone   = tab.id < activeTab || (tab.id === 3 && result);
            const isActive = tab.id === activeTab;
            const isLocked = tab.id === 3 && !result;
            return (
              <button
                key={tab.id}
                className={`ppf-step ${isActive ? "ppf-step--active" : ""} ${isDone ? "ppf-step--done" : ""} ${isLocked ? "ppf-step--locked" : ""}`}
                onClick={() => { if (!isLocked) setActiveTab(tab.id); }}
                disabled={isLocked}
              >
                <span className="ppf-step-bubble">
                  {isDone && tab.id !== 3 ? "✓" : tab.id + 1}
                </span>
                <span className="ppf-step-body">
                  <span className="ppf-step-label">{tab.icon} {tab.label}</span>
                  <span className="ppf-step-desc">{tab.desc}</span>
                </span>
              </button>
            );
          })}
          <div className="ppf-steps-track">
            <div className="ppf-steps-fill" style={{ width: `${(Math.min(activeTab, 3) / 3) * 100}%` }} />
          </div>
        </div>

        {/* ── PANEL ── */}
        <div className="ppf-panel-wrap">

          {/* ══ TAB 0: Investment ══ */}
          {activeTab === 0 && (
            <div className="ppf-panel" key="t0">
              <div className="ppf-panel-hd">
                <div className="ppf-panel-icon">💰</div>
                <div>
                  <h2>Investment Amount</h2>
                  <p>How much will you invest each year in your PPF account?</p>
                </div>
              </div>

              <div className="ppf-rate-card">
                <div className="ppf-rate-num">{PPF_RATE}<span>%</span></div>
                <div className="ppf-rate-body">
                  <strong>Current PPF Interest Rate</strong>
                  <span>Q1 FY 2026–27 · Declared by Govt. of India</span>
                  <span>Compounded annually · 100% tax-free interest</span>
                </div>
              </div>

              <div className="ppf-field">
                <label>
                  Annual Investment
                  <span className="ppf-tag">Max ₹1,50,000</span>
                </label>
                <div className="ppf-input-wrap">
                  <span className="ppf-input-sym">₹</span>
                  <input
                    type="number" min="500" max="150010" step="500"
                    value={form.yearlyInvestment}
                    onChange={(e) => upd("yearlyInvestment", Math.min(+e.target.value, 150010))}
                  />
                </div>
                <input
                  type="range" min="500" max="150010" step="500"
                  value={form.yearlyInvestment}
                  onChange={(e) => upd("yearlyInvestment", +e.target.value)}
                />
                <div className="ppf-range-row">
                  <span>₹500</span>
                  <span className="ppf-range-cur">{inr(yearly)}</span>
                  <span>₹1.5L</span>
                </div>
              </div>

              <div className="ppf-field">
                <label>Quick Presets</label>
                <div className="ppf-presets">
                  {[50010, 75001, 100000, 150010].map((v) => (
                    <button
                      key={v}
                      className={`ppf-preset ${form.yearlyInvestment === v ? "active" : ""}`}
                      onClick={() => upd("yearlyInvestment", v)}
                    >
                      {v === 150010 ? "₹1.5L (Max)" : `₹${(v / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ppf-info-row">
                <div className="ppf-info-cell">
                  <span>Monthly equivalent</span>
                  <strong>{inr(yearly / 12)} / mo</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Annual 80C tax saving (30%)</span>
                  <strong className="green">{inr(Math.min(yearly, 150010) * 0.3)}</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Over 15 years</span>
                  <strong>{inr(yearly * 15)}</strong>
                </div>
              </div>
            </div>
          )}

          {/* ══ TAB 1: Duration ══ */}
          {activeTab === 1 && (
            <div className="ppf-panel" key="t1">
              <div className="ppf-panel-hd">
                <div className="ppf-panel-icon">📅</div>
                <div>
                  <h2>Investment Duration</h2>
                  <p>PPF has a minimum 15-year lock-in. Extend in 5-year blocks after maturity.</p>
                </div>
              </div>

              <div className="ppf-field">
                <label>Base Tenure</label>
                <div className="ppf-tenure-grid">
                  {[15, 20, 25, 30].map((t) => (
                    <button
                      key={t}
                      className={`ppf-tenure-card ${form.tenure === t ? "active" : ""}`}
                      onClick={() => upd("tenure", t)}
                    >
                      {t === 15 && <span className="ppf-tenure-badge">Minimum</span>}
                      {t === 30 && <span className="ppf-tenure-badge ppf-tenure-badge--gold">Peak Wealth</span>}
                      <span className="ppf-tenure-num">{t}</span>
                      <span className="ppf-tenure-unit">Years</span>
                      <span className="ppf-tenure-sub">{inr(yearly * t)} invested</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="ppf-field">
                <label>Extension Blocks after 15 yrs <span className="ppf-tag">5 years each</span></label>
                <div className="ppf-toggles">
                  {[0, 1, 2, 3].map((b) => (
                    <button
                      key={b}
                      className={`ppf-toggle ${form.extensionBlocks === b ? "active" : ""}`}
                      onClick={() => upd("extensionBlocks", b)}
                    >
                      {b === 0 ? "None" : `+${b * 5} Yrs`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ppf-info-row">
                <div className="ppf-info-cell">
                  <span>Total Duration</span>
                  <strong>{form.tenure + form.extensionBlocks * 5} Years</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Total Deposits</span>
                  <strong>{inr(yearly * (form.tenure + form.extensionBlocks * 5))}</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Partial Withdrawal from</span>
                  <strong className="blue">Year 7 onwards</strong>
                </div>
              </div>

              <div className="ppf-timeline">
                <div className="ppf-tl-head">Key Milestones</div>
                {[
                  { yr: "Year 1",   dot: "neutral", label: "Account opens · First deposit" },
                  { yr: "Year 3–6", dot: "amber",   label: "Loan facility available (up to 25% of balance)" },
                  { yr: "Year 7",   dot: "green",   label: "Partial withdrawals allowed (50% of balance)" },
                  { yr: `Year ${form.tenure + form.extensionBlocks * 5}`, dot: "brand", label: "Maturity · Full withdrawal allowed" },
                ].map((item, i) => (
                  <div key={i} className="ppf-tl-row">
                    <div className={`ppf-tl-dot ppf-tl-dot--${item.dot}`} />
                    <div className="ppf-tl-text">
                      <strong>{item.yr}</strong>
                      <span>{item.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══ TAB 2: Profile ══ */}
          {activeTab === 2 && (
            <div className="ppf-panel" key="t2">
              <div className="ppf-panel-hd">
                <div className="ppf-panel-icon">👤</div>
                <div>
                  <h2>Your Profile</h2>
                  <p>Your age and investment timing refine the projection accuracy.</p>
                </div>
              </div>

              <div className="ppf-field">
                <label>Your Current Age</label>
                <div className="ppf-input-wrap">
                  <span className="ppf-input-sym" style={{ fontSize: "1rem" }}>🎂</span>
                  <input
                    type="number" min="18" max="55"
                    value={form.startAge}
                    onChange={(e) => upd("startAge", e.target.value)}
                  />
                </div>
                <input
                  type="range" min="18" max="55"
                  value={form.startAge}
                  onChange={(e) => upd("startAge", +e.target.value)}
                />
                <div className="ppf-range-row">
                  <span>18 yrs</span>
                  <span className="ppf-range-cur">{form.startAge} years old</span>
                  <span>55 yrs</span>
                </div>
              </div>

              <div className="ppf-field">
                <label>Investment Timing</label>
                <div className="ppf-mode-grid">
                  {[
                    {
                      id: "start",
                      icon: "🌅",
                      title: "Start of Year (April)",
                      desc: "Maximum interest earned — full-year compounding on each deposit",
                      badge: "Recommended",
                    },
                    {
                      id: "end",
                      icon: "🌆",
                      title: "End of Year (March)",
                      desc: "Slightly lower returns as deposit earns ~half-year interest",
                      badge: null,
                    },
                  ].map((m) => (
                    <button
                      key={m.id}
                      className={`ppf-mode-card ${form.investmentMode === m.id ? "active" : ""}`}
                      onClick={() => upd("investmentMode", m.id)}
                    >
                      <span className="ppf-mode-icon">{m.icon}</span>
                      <span className="ppf-mode-title">{m.title}</span>
                      <span className="ppf-mode-desc">{m.desc}</span>
                      {m.badge && <span className="ppf-mode-badge">{m.badge}</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ppf-info-row ppf-info-row--4">
                <div className="ppf-info-cell">
                  <span>Maturity Age</span>
                  <strong>{+form.startAge + form.tenure + form.extensionBlocks * 5} yrs</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Annual Investment</span>
                  <strong>{inr(yearly)}</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Total Duration</span>
                  <strong>{form.tenure + form.extensionBlocks * 5} Years</strong>
                </div>
                <div className="ppf-info-cell">
                  <span>Timing Mode</span>
                  <strong>{form.investmentMode === "start" ? "Start of Year" : "End of Year"}</strong>
                </div>
              </div>

              <div className="ppf-benefits">
                {[
                  ["🏛", "Sovereign guarantee — zero default risk"],
                  ["📋", "₹1.5L/yr fully deductible under Section 80C"],
                  ["💰", "Interest income is 100% tax-free"],
                  ["🔄", "Extendable indefinitely in 5-yr blocks"],
                ].map(([icon, text], i) => (
                  <div key={i} className="ppf-benefit">
                    <span className="ppf-benefit-icon">{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══ TAB 3: Results ══ */}
          {activeTab === 3 && result && (
            <div className="ppf-panel ppf-panel--results" key="t3">
              <div className="ppf-panel-hd">
                <div className="ppf-panel-icon">📊</div>
                <div>
                  <h2>Your PPF Projection</h2>
                  <p>Based on {PPF_RATE}% p.a. over {result.totalYears} years</p>
                </div>
                <button className="ppf-edit-btn" onClick={() => setActiveTab(0)}>↩ Edit Inputs</button>
              </div>

              {/* Maturity hero */}
              <div className="ppf-maturity-hero">
                <div className="ppf-maturity-main">
                  <div className="ppf-maturity-lbl">Total Maturity Amount</div>
                  <div className="ppf-maturity-val">{inr(result.maturity)}</div>
                  <div className="ppf-maturity-sub">
                    At age {result.startAge + result.totalYears} · {result.totalYears} years · {PPF_RATE}% p.a.
                  </div>
                </div>
                <div className="ppf-maturity-cards">
                  <div className="ppf-m-card">
                    <span className="ppf-m-lbl">Total Invested</span>
                    <span className="ppf-m-val blue">{inr(result.totalInvested)}</span>
                  </div>
                  <div className="ppf-m-card">
                    <span className="ppf-m-lbl">Interest Earned</span>
                    <span className="ppf-m-val green">{inr(result.totalInterest)}</span>
                  </div>
                  <div className="ppf-m-card">
                    <span className="ppf-m-lbl">Wealth Multiple</span>
                    <span className="ppf-m-val amber">{result.wealthMult}×</span>
                  </div>
                  <div className="ppf-m-card">
                    <span className="ppf-m-lbl">Tax Saving / Year*</span>
                    <span className="ppf-m-val">{inr(result.annualTaxSaving)}</span>
                  </div>
                </div>
              </div>

              {/* Breakdown */}
              <div className="ppf-section">
                <div className="ppf-section-hd">
                  <div className="ppf-section-icon">📈</div>
                  <h3>Growth Breakdown</h3>
                </div>
                <div className="ppf-section-bd">
                  {[
                    { label: "Total Amount Invested",    value: result.totalInvested,  color: "#2563eb", pct: (result.totalInvested  / result.maturity * 100) },
                    { label: "Interest Earned (100% Tax-Free)", value: result.totalInterest, color: "#394420", pct: (result.totalInterest / result.maturity * 100) },
                  ].map((item) => (
                    <div key={item.label} className="ppf-bar-row">
                      <div className="ppf-bar-top">
                        <span className="ppf-bar-dot" style={{ background: item.color }} />
                        <span className="ppf-bar-label">{item.label}</span>
                        <span className="ppf-bar-pct">{item.pct.toFixed(1)}%</span>
                        <strong className="ppf-bar-amt">{inr(item.value)}</strong>
                      </div>
                      <div className="ppf-bar-track">
                        <div className="ppf-bar-fill" style={{ width: `${item.pct}%`, background: item.color }} />
                      </div>
                    </div>
                  ))}

                  {/* Stacked */}
                  <div className="ppf-stacked-bar">
                    <div style={{ width: `${(result.totalInvested / result.maturity * 100).toFixed(1)}%`, background: "#2563eb" }} />
                    <div style={{ width: `${(result.totalInterest / result.maturity * 100).toFixed(1)}%`, background: "#394420" }} />
                  </div>
                  <div className="ppf-stacked-labels">
                    <span style={{ color: "#2563eb" }}>● Invested {(result.totalInvested / result.maturity * 100).toFixed(1)}%</span>
                    <span style={{ color: "#394420" }}>● Interest {result.interestPct}%</span>
                  </div>
                </div>
              </div>

              {/* Year-wise Table — FIXED: no horizontal scroll */}
              <div className="ppf-section">
                <div className="ppf-section-hd">
                  <div className="ppf-section-icon">📅</div>
                  <h3>Year-wise Growth Table</h3>
                </div>
                <div className="ppf-table-wrap">
                  <table className="ppf-table">
                    <thead>
                      <tr>
                        <th className="col-yr">Yr</th>
                        <th className="col-age">Age</th>
                        <th className="col-inv">Invested (yr)</th>
                        <th className="col-int">Interest</th>
                        <th className="col-cum">Cumulative</th>
                        <th className="col-bal">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, i) => (
                        <tr
                          key={i}
                          className={[
                            i === result.rows.length - 1 ? "tr-final" : "",
                            row.milestone ? "tr-milestone" : "",
                          ].join(" ")}
                        >
                          <td className="col-yr td-center">{row.year}</td>
                          <td className="col-age td-center td-muted">{row.age}</td>
                          <td className="col-inv td-right">{inr(row.invested)}</td>
                          <td className="col-int td-right td-green">{inr(row.interest)}</td>
                          <td className="col-cum td-right td-muted">{inr(row.cumInv)}</td>
                          <td className="col-bal td-right">
                            <span className="td-balance">{inr(row.balance)}</span>
                            {row.milestone && (
                              <span
                                className="ppf-ms-pill"
                                style={{ background: row.milestone.color + "18", color: row.milestone.color, borderColor: row.milestone.color + "40" }}
                              >
                                {row.milestone.label}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="ppf-note">
                * Tax saving estimated at 30% slab. Actual savings vary by income. Interest rate subject to quarterly revision by Govt. of India.
              </p>
            </div>
          )}

          {/* ── Footer Nav ── */}
          {activeTab < 3 && (
            <div className="ppf-footer">
              {activeTab > 0 && (
                <button className="ppf-btn ppf-btn--back" onClick={goPrev}>← Back</button>
              )}
              <button className="ppf-btn ppf-btn--next" onClick={goNext}>
                {activeTab === 2 ? "Calculate Maturity →" : "Continue →"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}